import { useCallback, useState } from 'react';
import { get } from 'lodash/fp';
import { Container, Virtualizer } from '@optimus-web/core';
import { getUrlParameter } from '@mews-commander/utils';
import { AccountBillAmounts } from '../types';
import { BillingMode } from '../constants';
import { BillCard } from './BillCard';

interface BillCardListProps extends Pick<Partial<AccountBillAmounts>, 'billAmounts'> {
    billIds: string[];
    mode?: BillingMode;
    accountId: string;
    containerHeight?: number;
}

// minimal height for the Virtualizer, to show least some bills if the height calculation somehow fails and returns 0
const DEFAULT_HEIGHT = 400;

export const BillCardList = ({ containerHeight, billIds, billAmounts, mode, accountId }: BillCardListProps) => {
    const renderBillCard = (billId: string) => (
        <Container key={billId} padding={['100', '0']}>
            <BillCard billId={billId} amount={get(billId, billAmounts)} mode={mode} accountId={accountId} />
        </Container>
    );

    const [height, setHeight] = useState(DEFAULT_HEIGHT);

    const billIdParam = getUrlParameter('billId') as string;
    const index = billIds.indexOf(billIdParam);

    const finalHeight = containerHeight ?? height;

    const calculateHeightFromRef = useCallback((node: HTMLDivElement) => {
        if (node !== null) {
            setHeight(window.innerHeight - node.getBoundingClientRect().top || DEFAULT_HEIGHT);
        }
    }, []);

    return billIds?.length < 10 ? (
        <>
            {billIds.map((billId) => (
                <BillCard
                    key={billId}
                    billId={billId}
                    amount={get(billId, billAmounts)}
                    mode={mode}
                    accountId={accountId}
                />
            ))}
        </>
    ) : (
        <div ref={calculateHeightFromRef}>
            <Virtualizer
                height={finalHeight}
                items={billIds.map(renderBillCard)}
                overscan={10}
                scrollToIndex={index === -1 ? undefined : index}
                estimatedSize={98}
            />
        </div>
    );
};
